import { useAuth0 } from "@auth0/auth0-react";
import { ArrowUpRightIcon, FolderIcon } from "@heroicons/react/24/outline";
import { ApiStatusToFileStatus, HIDDEN_FILE_KINDS, PRIORITIZE_PAGE_CONTENT } from "@utils/constants";
import { downloadLearnerFileById, getOptionValueList, matchOrCreateItems } from "@utils/helpers";
import { nonNullable } from "@utils/utils";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

import { Headline, MultiSelectItem, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerFileFileKindChoices,
  ApiLearnerFileStatusChoices,
  ApiReportSectionsAiGeneratedStatusChoices,
  ApiReportTemplateFileUploadConfigChoices,
  AssessmentToolType,
  LearnerFileType,
  ReportPageLocationEnums,
  ReportTypeEnums,
  useAddUpdateReportTreatmentPlanPrioritizeFieldsMutation,
  useAssessmentToolsQuery,
} from "@api/graphql/types-and-hooks";
import { InlineMultiSelectField } from "@components/BlurBasedFormFields";
import { useBlurBasedForm } from "@components/BlurBasedFormFields/useBlurBasedForm";
import { ConfirmGenerationDialog } from "@components/ConfirmGenerationDialog/ConfirmGenerationDialogV2";
import { DocumentReviewModal } from "@components/DocumentReviewModal/DocumentReviewModal";
import { EnhanceAccuracyDialog, getAccuracyContentList } from "@components/EnhanceAccuracyDialog/EnhanceAccuracyDialog";
import {
  ExistingFileWithMime,
  FileUploadClassifyAndParse,
} from "@components/UploadDocuments/FileUploadClassifyAndParse";
import { UploadDocumentsGuide } from "@components/UploadDocuments/UploadDocumentsGuide";
import { getDefaultTranscribeLanguage } from "@components/UploadDocuments/transcribeLanguage";
import { REASONS_TO_BE_OPEN, ValidationNoticeDialog } from "@components/ValidationNoticeDialog/ValidationNoticeDialog";
import { notifyError } from "@components/notifications/notifications";
import { FooterButtonRow } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/FooterButtonRow";
import {
  useGetFileParseStatusRetriever,
  useGetRemoveFileCallback,
  useGetReportFileUploadCompleteCallback,
  useGetS3BucketUploadCallback,
  useGetS3BucketUrlRetriever,
} from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/UploadFiles/hooks";
import {
  FilterSectionSubRoute,
  useGetFilteredPageSectionsByLocation,
} from "@pages/AssessmentReportV2Details/Components/helper";
import { useAdminData } from "@providers/AdminDataProvider";
import { useReport } from "@providers/ReportProvider";

type UploadFilesPrioritizeState = {
  safetyConcerns: MultiSelectItem[];
};

export const NewIAUploadFiles: React.FC = () => {
  const { report, reportQuery } = useReport();
  const { assessmentId: reportId } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const { isAcesUser, isTestAcesUser, isKuwaitUser } = useAdminData();
  const go = useNavigate();
  const [reviewFileId, setReviewFileId] = useState<string | null>(null);
  const [promptDialogOpen, setPromptDialogOpen] = useState<boolean>(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState<REASONS_TO_BE_OPEN | null>(null);
  const [accuracyDialogOpen, setAccuracyDialogOpen] = useState<boolean>(false);
  const [accuracyDialogContent, setAccuracyDialogContent] = useState<string[]>([]);
  const [showAccuracyContentWarning] = useLocalStorage("show-accuracy-content-warning", true);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasHydratedRef = useRef<boolean>(false);
  const [orderedFileNames, setOrderedFileNames] = useState<string[]>([]);

  const { refetch } = reportQuery;

  const files = useMemo(
    () => report.reportFiles.edges?.map((edge) => edge?.node).filter(nonNullable),
    [report.reportFiles],
  );

  const filesForAssessment = useMemo(
    () => files?.filter((file) => !HIDDEN_FILE_KINDS.includes(file.fileKind as ApiLearnerFileFileKindChoices)) ?? [],
    [files],
  );

  const stillProcessing = files?.some(
    (f) =>
      f.status === ApiLearnerFileStatusChoices.Processing ||
      f.status === ApiLearnerFileStatusChoices.Pending ||
      f.status === ApiLearnerFileStatusChoices.Classifying ||
      f.status === ApiLearnerFileStatusChoices.ClassifyingSuccess,
  );

  const { data: assessmentToolsData } = useAssessmentToolsQuery({ reportType: ReportTypeEnums.InitialAssessment });
  const supportedAssessmentsList: AssessmentToolType[] = useMemo(
    () => assessmentToolsData?.assessmentTools?.filter((item) => !!item).filter((tool) => tool.parsingSupported) ?? [],
    [assessmentToolsData],
  );
  const assessmentIds = useMemo(() => new Set(supportedAssessmentsList.map((a) => a.id)), [supportedAssessmentsList]);

  // Accept both INTAKE_CALL (legacy records) and INTERVIEW (classify mode) — same set
  // also drives the language-picker lock, so any interview recording, old or new, counts.
  const existingIntakeCalls = useMemo(
    () =>
      filesForAssessment.filter(
        (f) =>
          f.fileKind === ApiLearnerFileFileKindChoices.IntakeCall ||
          f.fileKind === ApiLearnerFileFileKindChoices.Interview,
      ),
    [filesForAssessment],
  );
  const existingDiagnosis = useMemo(
    () => filesForAssessment.find((f) => f.fileKind === ApiLearnerFileFileKindChoices.DiagnosisReport),
    [filesForAssessment],
  );
  const existingBHTIAFiles = useMemo(
    () => filesForAssessment.filter((f) => f.fileKind === ApiLearnerFileFileKindChoices.Bhtia),
    [filesForAssessment],
  );

  const reportType = report.reportType;
  const fileUploadConfig = report.reportTemplate?.fileUploadConfig;
  const learnerIdForReport = report.learner?.id;
  const learnerName =
    `${report.reportClientDetails?.clientFirstName ?? ""} ${report.reportClientDetails?.clientLastName ?? ""}`.trim() ||
    undefined;
  const isCatalightTemplate = fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight.valueOf();
  const isAces = isAcesUser || isTestAcesUser;

  // ACES and Catalight keep their org-specific intros (they list the exact doc
  // set we expect for that flow). Every other org gets the unified copy. All
  // three end with the same pointer to /downloads (the "Assessment Templates"
  // page in the global sidebar's Resources section).
  const autoCategorizeSentence = "Files are automatically categorized; adjust any category as needed.";
  const scoringTemplatesSentence = (
    <>
      Frontera&apos;s assessment scoring templates are available to download{" "}
      <Link to="/downloads" className="text-interactive-primary-default underline">
        here
      </Link>
      .
    </>
  );

  let introParagraph: React.ReactNode;
  if (isAces) {
    introParagraph = (
      <Paragraph size="small" className="text-text-tertiary">
        Upload your interview recording, observation notes, ACES Intake Form or BHTIA, diagnostic report, and assessment
        files. {autoCategorizeSentence} {scoringTemplatesSentence}
      </Paragraph>
    );
  } else if (isCatalightTemplate) {
    introParagraph = (
      <Paragraph size="small" className="text-text-tertiary">
        Upload your interview recording, BHTIA, diagnostic report, behavioral observations, and assessment files.{" "}
        {autoCategorizeSentence} {scoringTemplatesSentence}
      </Paragraph>
    );
  } else {
    introParagraph = (
      <Paragraph size="small" className="text-text-tertiary">
        Upload your files: assessments, records, notes, and more. {autoCategorizeSentence} {scoringTemplatesSentence}
      </Paragraph>
    );
  }

  const nextPage: FilterSectionSubRoute = "report-part-1";
  const nextPageFilteredSections = useGetFilteredPageSectionsByLocation(nextPage);
  const nextPageAlreadyGenerated = nextPageFilteredSections.some(
    (section) =>
      section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending ||
      section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPredicted,
  );

  const S3BucketUrlRetriever = useGetS3BucketUrlRetriever({
    transcribeLanguage: getDefaultTranscribeLanguage(isKuwaitUser),
  });
  const reportFileUploadCompleteCallback = useGetReportFileUploadCompleteCallback({ mode: "classify" });
  const S3BucketUploadCallback = useGetS3BucketUploadCallback();
  const fileParseStatusRetriever = useGetFileParseStatusRetriever();
  const removeFileCallback = useGetRemoveFileCallback();

  const existingFilesMapped: ExistingFileWithMime[] = filesForAssessment.map((existingFile) => ({
    fileName: existingFile.originalFileName || "<missing-file-name>",
    fileSize: existingFile.fileSize,
    fileStatus: ApiStatusToFileStatus[existingFile.status || ApiLearnerFileStatusChoices.Pending],
    metadata: existingFile.metadata,
    mimeType: existingFile.mimeType,
    fileKind: existingFile.fileKind || "<missing-file-kind>",
    fileId: existingFile.id,
    failureReason: existingFile.processingFailureReason || "",
    failureCode: existingFile.processingFailureCode,
  }));

  const { safetyConcern } = PRIORITIZE_PAGE_CONTENT;
  const reportTreatmentPlanPrioritizeFields = report.reportTreatmentPlanPrioritizeFields;
  const [selectedSafetyConcern, setSelectedSafetyConcern] = useState<MultiSelectItem[] | []>([]);
  const addUpdatePrioritizeMutation = useAddUpdateReportTreatmentPlanPrioritizeFieldsMutation({});

  useEffect(() => {
    if (!reportTreatmentPlanPrioritizeFields) return;
    if (reportTreatmentPlanPrioritizeFields.safetyConcerns) {
      setSelectedSafetyConcern(
        matchOrCreateItems(
          reportTreatmentPlanPrioritizeFields.safetyConcerns as string[],
          safetyConcern.optionItems as MultiSelectItem[],
        ),
      );
    }
    hasHydratedRef.current = true;
  }, [reportTreatmentPlanPrioritizeFields, safetyConcern.optionItems]);

  const savePrioritize = useCallback(
    async (overrides?: Partial<UploadFilesPrioritizeState>) => {
      const safetyConcerns = overrides?.safetyConcerns ?? selectedSafetyConcern;
      try {
        await addUpdatePrioritizeMutation.mutateAsync({
          reportId: reportId ? reportId : "missing-report-id",
          prioritizeFieldsDetails: {
            familyLife: reportTreatmentPlanPrioritizeFields?.familyLife ?? [],
            communicationAbility: reportTreatmentPlanPrioritizeFields?.communicationAbility ?? [],
            engagementAbility: reportTreatmentPlanPrioritizeFields?.engagementAbility ?? false,
            socialDesires: reportTreatmentPlanPrioritizeFields?.socialDesires,
            safetyConcerns: safetyConcerns.length ? getOptionValueList(safetyConcerns) : [],
          },
        });
      } catch (error) {
        console.error("Error Saving Challenging Behaviors", error);
        notifyError("Error Saving Challenging Behaviors");
        throw error;
      }
    },
    [selectedSafetyConcern, reportId, reportTreatmentPlanPrioritizeFields, addUpdatePrioritizeMutation],
  );

  const setters = { safetyConcerns: setSelectedSafetyConcern };
  const normalizeValue = <K extends keyof UploadFilesPrioritizeState>(
    key: K,
    value: unknown,
  ): UploadFilesPrioritizeState[K] => {
    if (key === "safetyConcerns") {
      return (Array.isArray(value) ? value : []) as UploadFilesPrioritizeState[K];
    }
    return value as UploadFilesPrioritizeState[K];
  };

  const state: UploadFilesPrioritizeState = { safetyConcerns: selectedSafetyConcern };

  const { handleFieldSave } = useBlurBasedForm<UploadFilesPrioritizeState>({
    containerRef,
    mutationFn: savePrioritize,
    isSaveReady: () => hasHydratedRef.current,
    state,
    setters,
    normalizeValue,
  });

  const handleNext = async () => {
    await savePrioritize();
    if (nextPageAlreadyGenerated) {
      go(`../${nextPage}`);
      return;
    }
    try {
      const updatedFiles = (await refetch()).data?.getReports?.edges?.[0]?.node?.reportFiles.edges?.map(
        (edge) => edge?.node,
      );
      const filesForCheck = updatedFiles?.filter((f) => f?.status !== "PENDING")?.filter(nonNullable);
      const existingAssessments = filesForCheck?.filter((file) => assessmentIds.has(file.fileKind)) ?? [];

      // ClassifyingSuccess is busy: it's the gap between classification finishing and
      // parsing kicking off (mirrors isFileBusy in FileUploadClassifyAndParse).
      const stillBusy = filesForCheck?.some(
        (f) =>
          f.status === ApiLearnerFileStatusChoices.Processing ||
          f.status === ApiLearnerFileStatusChoices.Pending ||
          f.status === ApiLearnerFileStatusChoices.Classifying ||
          f.status === ApiLearnerFileStatusChoices.ClassifyingSuccess,
      );
      const errorFiles = filesForCheck?.filter(
        (f) =>
          f.status === ApiLearnerFileStatusChoices.ProcessError ||
          f.status === ApiLearnerFileStatusChoices.ClassifyingError,
      );

      // Order matters: in classify mode fileKind stays UNCLASSIFIED until classification
      // completes, so an empty existingAssessments mid-flight is FILES_VALIDATING — not
      // MISSING_FILES. Check busy/errors before the empty-set fallthrough.
      if (stillBusy) {
        setValidationDialogOpen("FILES_VALIDATING");
        return;
      }
      if (errorFiles && errorFiles.length > 0) {
        setValidationDialogOpen("PARSING_FAILED");
        return;
      }
      if (existingAssessments.length === 0) {
        setValidationDialogOpen("MISSING_FILES");
        return;
      }

      const tempAccuracyContentList = getAccuracyContentList({
        // Assessments are required and already validated by the MISSING_FILES guard above, so this branch is unreachable with length===0.
        existingAssessments: false,
        existingDiagnosis: !existingDiagnosis,
        existingBHTIAFiles: isCatalightTemplate && existingBHTIAFiles?.length === 0,
        existingIntakeCalls: existingIntakeCalls?.length === 0,
        existingChallengingBehavior: selectedSafetyConcern?.length === 0,
      });

      if (tempAccuracyContentList.length > 0 && showAccuracyContentWarning) {
        setAccuracyDialogContent(tempAccuracyContentList);
        setAccuracyDialogOpen(true);
        return;
      }
      setPromptDialogOpen(true);
    } catch (error) {
      console.error("Assessment file retrieval error: ", error);
      notifyError("Assessment file retrieval error");
    }
  };

  return (
    <div className="flex w-full flex-col gap-y-4 pb-10 pt-6" ref={containerRef}>
      <ValidationNoticeDialog reasonToBeOpen={validationDialogOpen} closeDialog={() => setValidationDialogOpen(null)} />
      <ConfirmGenerationDialog
        type={ReportPageLocationEnums.ReportPart_1}
        promptDialogOpen={promptDialogOpen}
        setPromptDialogOpen={setPromptDialogOpen}
      />
      <EnhanceAccuracyDialog
        accuracyDialogContent={accuracyDialogContent}
        accuracyDialogOpen={accuracyDialogOpen}
        setAccuracyDialogOpen={setAccuracyDialogOpen}
        onContinue={() => setPromptDialogOpen(true)}
      />
      <div className="w-full rounded-xl border border-border-strong bg-white p-6">
        <div className="flex flex-col mb-4">
          <Headline size="medium">Upload Files</Headline>
          {introParagraph}
        </div>
        <div className="mb-4">
          <UploadDocumentsGuide reportType={reportType} fileUploadConfig={fileUploadConfig} />
        </div>
        <FileUploadClassifyAndParse
          maxSize={"2GB"}
          existingFiles={existingFilesMapped}
          supportedAssessments={supportedAssessmentsList}
          S3BucketUrlRetriever={S3BucketUrlRetriever}
          S3BucketUploadCallback={S3BucketUploadCallback}
          reportFileUploadCompleteCallback={reportFileUploadCompleteCallback}
          fileParseStatusRetriever={fileParseStatusRetriever}
          removeFileCallback={removeFileCallback}
          allowFileDownload={true}
          downloadFileCallback={async (fileId) => {
            const token = await getAccessTokenSilently();
            await downloadLearnerFileById({ fileId: fileId, token: token });
          }}
          onOrderChanged={(fileNames) => {
            setOrderedFileNames((prev) =>
              prev.length === fileNames.length && prev.every((name, i) => name === fileNames[i]) ? prev : fileNames,
            );
          }}
          previewFileCallback={async (fileId) => {
            setReviewFileId(fileId);
          }}
          reportType={reportType}
          fileUploadConfig={fileUploadConfig}
          reportId={report.id}
          learnerId={learnerIdForReport}
        />
        {learnerIdForReport && (
          <Link
            to={`/clients/${learnerIdForReport}/file-library`}
            className="mt-8 inline-flex w-fit items-center gap-1.5 text-sm text-interactive-primary-default underline hover:text-interactive-primary-hover"
          >
            <FolderIcon aria-hidden="true" className="h-4 w-4" />
            {learnerName ? (
              <>
                View all of <span className="ph-no-capture">{learnerName}</span>&apos;s files
              </>
            ) : (
              "View all files"
            )}
            <ArrowUpRightIcon aria-hidden="true" className="h-3.5 w-3.5" />
          </Link>
        )}
        {reviewFileId && (
          <DocumentReviewModal
            fileId={reviewFileId}
            existingFiles={[...(filesForAssessment as LearnerFileType[])].sort(
              (a, b) => orderedFileNames.indexOf(a.originalFileName) - orderedFileNames.indexOf(b.originalFileName),
            )}
            supportedAssessments={supportedAssessmentsList}
            onClose={() => setReviewFileId(null)}
            setFileId={(fileId) => setReviewFileId(fileId)}
          />
        )}
      </div>
      <div className="flex flex-col rounded-xl border border-border-strong bg-white p-6">
        <div className="flex flex-col mb-4">
          <div className="flex items-baseline justify-between">
            <Headline size="medium">Challenging Behaviors</Headline>
            <Paragraph size="small" className="text-text-tertiary">
              Optional
            </Paragraph>
          </div>
          <Paragraph size="small" className="text-text-tertiary">
            Let us know about any behaviors you're tracking that may need an FBA. Choose from our list of common
            behaviors or add your own.
          </Paragraph>
        </div>
        <InlineMultiSelectField<UploadFilesPrioritizeState>
          value={selectedSafetyConcern}
          fieldKey="safetyConcerns"
          onSave={handleFieldSave}
          title="Challenging Behaviors"
          labelHidden
          // The field is optional, but we render the "Optional" indicator next to the card's Headline above instead of inline; `required` suppresses the InlineMultiSelectField's built-in hint to avoid showing "Optional" twice.
          required
          multiSelectItems={safetyConcern.optionItems as MultiSelectItem[]}
          multiSelectMode="tags"
        />
      </div>
      <FooterButtonRow
        primaryButtonDisabled={reportQuery.isFetching || stillProcessing}
        primaryButtonAction={handleNext}
        skipNavigate
      />
    </div>
  );
};
